import {inject, Injectable, NgZone} from '@angular/core';
import {ApiService} from "../../../../core/api/api.service";
import {BehaviorSubject, catchError, map, Observable, of, switchMap, timeout} from "rxjs";
import {AnalysisStatusEnum} from "../../../../core/models/analysis-status";
import {ReferenceApiService} from "../../../../core/api/reference-api/reference-api.service";
import * as Sentry from "@sentry/angular";
import {ConfigService} from "../../../../core/config";
import {CbdResultDto} from "../../../../core/dto/cbd-result-dto";
import {Picture} from "../../../../core/models/picture";
import {DomSanitizer} from "@angular/platform-browser";
import {TyrePicture} from "../../../../core/models/tyrePicture";
import {isEqual} from "lodash";
import {Step, StepType} from "../../../../core/models/step";

@Injectable({
    providedIn: 'root'
})
export class PhotoshootService {

  private readonly _sessionStorage = sessionStorage;

  protected api = inject(ApiService);
  protected referenceApi = inject(ReferenceApiService);
  protected config = inject(ConfigService);
  protected ngZone = inject(NgZone);
  private sanitizer = inject(DomSanitizer);

  private readonly _pictures: BehaviorSubject<TyrePicture[]> = new BehaviorSubject<TyrePicture[]>([]);
  readonly pictures$ = this._pictures.asObservable();

  private static readonly CBD_TIMEOUT = 5000;

  sendCBDWear(tyreAnalysisId: number, blob: Blob) {
      return this.referenceApi.createCBDWear(tyreAnalysisId).pipe(switchMap((response) => {
                  return this.api.uploadFile(new File([blob], response.uploadUrl.fileName), response.uploadUrl.url)
                      .pipe(map(() => response))
              }
          ),
          switchMap((cbdDto) => this.referenceApi.patchCBDWear(cbdDto.id, AnalysisStatusEnum.UPLOADED).pipe(map(() => cbdDto))),
          switchMap((cbdDto) => {
            if (this.config.getWebapp().enableCBDViews) {
              return this.setMercureEventSource(tyreAnalysisId, cbdDto.sseTyreAnalysisToken)
            } else {
              return of({tyreAnalysisId, isCBDValid: undefined})
            }
          }),
          catchError((error) => {
              Sentry.captureMessage('Unable to send wear picture for CBD', {extra: {error}});
              return of({tyreAnalysisId, isCBDValid: undefined});
          }),
          timeout(PhotoshootService.CBD_TIMEOUT)
        )
  }

  sendCBDRef(tyreAnalysisId: number, blob: Blob) {
      return this.referenceApi.createCBDRef(tyreAnalysisId).pipe(switchMap((response) => {
                  return this.api.uploadFile(new File([blob], response.uploadUrl.fileName), response.uploadUrl.url)
                      .pipe(map(() => response))
              }
          ),
          switchMap((cbdDto) => this.referenceApi.patchCBDRef(cbdDto.id, AnalysisStatusEnum.UPLOADED).pipe(map(() => cbdDto))),
          switchMap((cbdDto) => {
            if (this.config.getWebapp().enableCBDViews) {
              return this.setMercureEventSource(tyreAnalysisId, cbdDto.sseTyreAnalysisToken)
            } else {
              return of({tyreAnalysisId, isCBDValid: undefined})
            }
          }),
          catchError((error) => {
              Sentry.captureMessage('Unable to send ref picture for CBD', {extra: {error}});
              return of({tyreAnalysisId, isCBDValid: undefined});
          }),
        timeout(PhotoshootService.CBD_TIMEOUT))
  }

  private setMercureEventSource(tyreAnalysisId: number, token: string): Observable<CbdResultDto> {
      return new Observable((observer) => {
          const url = new URL(this.config.mercureUrl);
          const topic = this.getMercureTopic(this.config.getConfig().app.environment, tyreAnalysisId);
          url.searchParams.append('topic', topic);
          url.searchParams.append(
              'authorization',
              token
          );
          const eSource = new EventSource(url.toString(), {
              withCredentials: false
          });
          eSource.onerror = (event) => {
              Sentry.captureException(new Error('CBD Mercure event error'), {extra: {event}});
              observer.error(new Error('CBD Mercure event error'));
          };
          eSource.onmessage = (event) => {
              this.ngZone.run(() => {
                  const result: CbdResultDto = JSON.parse(event.data)
                  observer.next(result);
                  observer.complete();
              })
              eSource.close();
          }

          return () => {
              eSource.close();
          }
      })
  }

  private getMercureTopic(env: string, tyreAnalysisId: number) {
      let fEnv: string | undefined = env;
      switch (env) {
          case 'local':
              fEnv = 'val';
              break;
          case 'prod':
              fEnv = undefined;
      }
      return [fEnv, 'tyre-analyses', tyreAnalysisId].filter(Boolean).join('/');
  }

  async associatePictureBlob(picture: Picture, blob: Blob) {
    const buffer = await blob.arrayBuffer();
    picture.data = new Uint8Array(buffer.slice(0));
    picture.url = this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(blob));
    return picture;
  }

  getPictures(): TyrePicture[] {
    return this._pictures ? this._pictures.getValue() : [];
  }

  setPicture(picture: TyrePicture) {
    const pictures = this.getUpdatedPictures(picture, this.getPictures());
    this._pictures.next(pictures);
  }

  setPictures(p: TyrePicture[]) {
    let pictures = this.getPictures();
    for (let picture of p) {
      pictures = this.getUpdatedPictures(picture, pictures);
    }
    this._pictures.next(pictures);
  }

  clearPictures() {
    this._pictures.next([]);
  }

  private getUpdatedPictures(picture: TyrePicture, savedPictures: TyrePicture[]) {
    let pictures = savedPictures;
    const existingPicture = pictures.find(p => isEqual(p.step, picture.step));
    if (existingPicture) {
      existingPicture.url = picture.url;
      existingPicture.data = picture.data;
      existingPicture.status = picture.status;
    } else {
      pictures.push(picture);
    }
    return pictures;
  }

  // TODO: Do not use session storage directly here.
  setCamera(deviceId: string, step: StepType) {
    this._sessionStorage.setItem(`camera.${step}`, deviceId)
  }

  getCamera(step: StepType) {
    return this._sessionStorage.getItem(`camera.${step}`);
  }

}
